import React, { useCallback, useState, useRef, useEffect } from "react";
import { SearchBox } from "@mapbox/search-js-react";
import { US_STATES } from "../utils/usStates";
import useIpData from "../hooks/useIpData";
import styled from "@emotion/styled";
import { Input } from "./common";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;

  /* CSS to detect autofill */
  input:-webkit-autofill {
    animation-name: onAutoFillStart;
    animation-duration: 0.001s;
  }

  input:not(:-webkit-autofill) {
    animation-name: onAutoFillCancel;
    animation-duration: 0.001s;
  }

  @keyframes onAutoFillStart {
    from { /* empty */ }
    to { /* empty */ }
  }

  @keyframes onAutoFillCancel {
    from { /* empty */ }
    to { /* empty */ }
  }
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
`;
const InputCol = styled.div`
  width: 100%;
  flex-direction: column;
`;
const Label = styled.label`
  font-size: 0.9rem;
  margin-bottom: 5px;
  font-weight: 500;
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Arrow = styled.span`
  pointer-events: none;
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #888;
`;

const Select = styled.select`
  display: flex;
  flex: 1;
  font-size: 14px;
  padding: 10px;
  border: 1px solid ${({ error }) => (error ? 'red' : '#ddd')};
  margin-bottom: 10px;
  box-sizing: border-box;
  height: 40px;
  background: white;
  border-radius: 5px;
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  box-shadow: none;
  outline: none;
`;
const ErrorMsg = styled.div`
  color: red;
  font-size: 12px;
  margin-bottom: 10px;
`;
const SaveButton = styled.button`
  background-color: white;
  color: black;
  border: 1px solid black;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 16px;
  text-align: center;
  width: 100%;
  border-radius: 50px;
`;
const CancelButton = styled.button`
  font-size: 14px;
  color: black;
  background-color: transparent;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 15px;
`;

// Custom hook for SearchBox autofill detection
  const useSearchBoxAutofill = (searchBoxRef) => {

    useEffect(() => {
      const setupAutofillDetection = () => {
        const input = searchBoxRef.current?.querySelector('input[aria-label="Street Address"]');
        if (!input) return false;

        // Enable proper autocomplete
        input.setAttribute('autocomplete', 'street-address');
        input.setAttribute('name', 'street-address');
        input.setAttribute('id', 'street-address');

        // Initialize tracking variables on the input element
        if (input._lastKnownValue === undefined) {
          input._lastKnownValue = input.value || '';
        }

        const handleAutofill = () => {
          const currentValue = input.value;
          const now = Date.now();

          // Track if the actual input DOM value changed (not the React state)
          const inputValueChanged = currentValue !== input._lastKnownValue;

          // Check if this looks like autofill (significant change in value length or no recent user activity)
          const significantChange = Math.abs(currentValue.length - (input._lastKnownValue || '').length) > 3;
          const noRecentUserActivity = !input._lastUserActivity || (now - input._lastUserActivity) > 500;
          const looksLikeAutofill = inputValueChanged && (significantChange || noRecentUserActivity);


          // Only trigger autofill detection if the input value changed in a way that looks like autofill
          if (currentValue.length > 0 &&
              looksLikeAutofill &&
              !input._userHasSelected &&
              !input._hasTriggeredAutofill) {

            input._hasTriggeredAutofill = true;

            setTimeout(() => {
              if (input._userHasSelected) return;

              input.focus();
              const originalValue = input.value;

              // Trigger SearchBox dropdown with realistic events
              input.value = originalValue.slice(0, -1);
              input.dispatchEvent(new InputEvent('input', {
                bubbles: true,
                cancelable: true,
                inputType: 'deleteContentBackward',
                data: null
              }));

              setTimeout(() => {
                if (input._userHasSelected) {
                  input.value = originalValue;
                  return;
                }

                input.value = originalValue;
                input.dispatchEvent(new InputEvent('input', {
                  bubbles: true,
                  cancelable: true,
                  inputType: 'insertText',
                  data: originalValue.slice(-1)
                }));

                input.dispatchEvent(new FocusEvent('focus', { bubbles: true }));
              }, 100);
            }, 100);

            // Reset flag after some time
            setTimeout(() => {
              input._hasTriggeredAutofill = false;
            }, 5000);
          }

          // Always update the last known input value
          input._lastKnownValue = currentValue;
        };

        // Use input event listener specifically for the street address input
        const handleInputEvent = (e) => {
          // Only handle events that are actually from this input
          if (e.target === input) {
            // Track user activity timestamp for regular input events
            if (e.inputType && e.inputType !== '') {
              input._lastUserActivity = Date.now();
              input._lastKnownValue = input.value;
            } else {
              // Empty inputType might be autofill - check after a short delay
              setTimeout(handleAutofill, 10);
            }
          }
        };

        // Track user interaction events to distinguish from autofill
        const trackUserActivity = () => {
          input._lastUserActivity = Date.now();
        };

        input.addEventListener('input', handleInputEvent);
        input.addEventListener('keydown', trackUserActivity);
        input.addEventListener('keyup', trackUserActivity);
        input.addEventListener('paste', trackUserActivity);
        input.addEventListener('touchstart', trackUserActivity);
        input.addEventListener('touchend', trackUserActivity);

        // More frequent interval to catch autofill faster
        const checkInterval = setInterval(handleAutofill, 200);

        const handleAnimationStart = (e) => {
          // Only handle animation events from this specific input
          if (e.target === input && e.animationName === 'onAutoFillStart') {
            setTimeout(handleAutofill, 10);
          }
        };

        input.addEventListener('animationstart', handleAnimationStart);

        return () => {
          clearInterval(checkInterval);
          input.removeEventListener('input', handleInputEvent);
          input.removeEventListener('keydown', trackUserActivity);
          input.removeEventListener('keyup', trackUserActivity);
          input.removeEventListener('paste', trackUserActivity);
          input.removeEventListener('touchstart', trackUserActivity);
          input.removeEventListener('touchend', trackUserActivity);
          input.removeEventListener('animationstart', handleAnimationStart);
        };
      };

      let cleanup;
      const interval = setInterval(() => {
        cleanup = setupAutofillDetection();
        if (cleanup) clearInterval(interval);
      }, 100);

      return () => {
        clearInterval(interval);
        if (cleanup) cleanup();
      };
    }, [searchBoxRef]);
  };

const AddressForm = ({
  onSave,
  onCancel,
  existingEmail,
  showSaveButton = true,
  showCancelButton = true,
  onChange,
  showRecipientsName = true,
  addressName = "Shipping Address",
}) => {
  // Group related state together
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailAddress: "",
    streetAddress: "",
    addressOptional: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [formErrors, setFormErrors] = useState({
    emailError: "",
    validationErrors: {
      firstName: false,
      lastName: false,
      streetAddress: false,
      city: false,
      state: false,
      zipCode: false,
    },
  });

  const searchBoxRef = useRef(null);
  const { latitude, longitude } = useIpData();
  const emailRegex = React.useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/, []);

  // Helper functions for cleaner state updates
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const setEmailError = useCallback((error) => {
    setFormErrors(prev => ({ ...prev, emailError: error }));
  }, []);

  const setValidationErrors = useCallback((errors) => {
    setFormErrors(prev => ({
      ...prev,
      validationErrors: { ...prev.validationErrors, ...errors }
    }));
  }, []);

  // Validation functions
  const validateForm = useCallback(() => {
    const { firstName, lastName, streetAddress, city, state, zipCode, emailAddress } = formData;

    return {
      firstName: showRecipientsName ? !!firstName : true,
      lastName: showRecipientsName ? !!lastName : true,
      streetAddress: !!streetAddress,
      city: !!city,
      state: !!state,
      zipCode: !!zipCode,
      email: existingEmail ? true : !!emailAddress && emailRegex.test(emailAddress),
    };
  }, [formData, showRecipientsName, existingEmail, emailRegex]);

  const buildAddressData = useCallback(() => {
    const { firstName, lastName, streetAddress, addressOptional, city, state, zipCode, emailAddress } = formData;

    return {
      address_name: showRecipientsName ? `${firstName} ${lastName}`.trim() : "",
      first_name: showRecipientsName ? firstName : "",
      last_name: showRecipientsName ? lastName : "",
      address: `${streetAddress}, ${city}, ${state} ${zipCode}, United States`,
      address_optional: addressOptional,
      email: existingEmail || emailAddress,
      postalCode: zipCode,
      city,
      state,
      country: "United States",
      street_address: streetAddress,
    };
  }, [formData, showRecipientsName, existingEmail]);

  // Event handlers
  const handleZipCodeChange = useCallback((e) => {
    const sanitized = e.target.value.replace(/[^\d-]/g, '');
    updateField('zipCode', sanitized);
  }, [updateField]);

  const handleSave = useCallback(() => {
    const validation = validateForm();
    setValidationErrors(validation);

    if (Object.values(validation).some((isValid) => !isValid)) {
      const fields = showRecipientsName
        ? " (First Name, Last Name, Street Address, City, State, ZIP Code)"
        : " (Street Address, City, State, ZIP Code)";
      alert("Please fill in all required address fields" + fields);
      return;
    }

    if (!existingEmail) {
      const { emailAddress } = formData;
      if (!emailAddress) {
        setEmailError("Email is required");
        return;
      }
      if (!emailRegex.test(emailAddress)) {
        setEmailError("Please enter a valid email address");
        return;
      }
    }

    setEmailError("");
    onSave?.(buildAddressData());
  }, [validateForm, setValidationErrors, showRecipientsName, existingEmail, formData, emailRegex, setEmailError, onSave, buildAddressData]);

  // Apply autofill detection
  useSearchBoxAutofill(searchBoxRef);
  const previousDataRef = useRef();

  // Handle form data changes and notify parent
  useEffect(() => {
    if (typeof onChange === "function") {
      const addressData = buildAddressData();
      const validation = validateForm();
      const isFormValid = Object.values(validation).every(Boolean);

      // Only call onChange if the data has actually changed
      const currentData = JSON.stringify({ addressData, isFormValid });
      if (previousDataRef.current !== currentData) {
        previousDataRef.current = currentData;
        onChange({
          address: addressData,
          valid: isFormValid,
        });
      }
    }
  // onChange not needed, will cause loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, showRecipientsName, existingEmail, buildAddressData, validateForm]);

  return (
    <FormContainer>
      {!existingEmail && (
        <>
          <Label style={{ color: "gray" }}>Recipient's Email</Label>
          <Input
            type="email"
            value={formData.emailAddress}
            onChange={(e) => updateField('emailAddress', e.target.value.trim())}
            error={!!formErrors.emailError}
            placeholder="Email address"
            autoComplete="email"
            name="email"
            id="email"
          />
          {formErrors.emailError && <ErrorMsg>{formErrors.emailError}</ErrorMsg>}
        </>
      )}
      {showRecipientsName && (
        <Row>
          <InputCol>
            <Label>First Name*</Label>
            <Input
              type="text"
              value={formData.firstName}
              onChange={(e) => updateField('firstName', e.target.value)}
              error={formErrors.validationErrors.firstName}
              placeholder="First Name"
              autoComplete="given-name"
              name="given-name"
              id="given-name"
            />
            {formErrors.validationErrors.firstName && <ErrorMsg>Please enter the recipient's first name</ErrorMsg>}
          </InputCol>
          <InputCol>
            <Label>Last Name*</Label>
            <Input
              type="text"
              value={formData.lastName}
              onChange={(e) => updateField('lastName', e.target.value)}
              error={formErrors.validationErrors.lastName}
              placeholder="Last Name"
              autoComplete="family-name"
              name="family-name"
              id="family-name"
            />
            {formErrors.validationErrors.lastName && <ErrorMsg>Please enter the recipient's last name</ErrorMsg>}
          </InputCol>
        </Row>
      )}
      <Label style={{ marginTop: "15px" }}>{addressName}</Label>
      <div css={{ border: formErrors.validationErrors.streetAddress ? '1px solid red' : '1px solid #ddd', borderRadius: 5 }} ref={searchBoxRef}>
        <SearchBox
          accessToken="pk.eyJ1Ijoic3RldmVuanVuZyIsImEiOiJjbThnNTkyNTEwam9zMm1xNmhxZXAwcDkxIn0.ADkYSXUtJuwyTg8tryo_Ug"
          placeholder="Street Address"
          options={{
            language: "en",
            country: "us",
            types: "address",
            proximity: latitude ? [longitude, latitude] : 'ip',
            limit: 5,
          }}
          theme={{
            variables: {
              boxShadow: "none",
              border: "none",
            },
          }}
          onClear={() => {
            // Reset selection flag when clearing
            const input = searchBoxRef.current?.querySelector('input[aria-label="Street Address"]');
            if (input) {
              input._userHasSelected = false;
            }
            updateField('streetAddress', '');
          }}
          onRetrieve={(res) => {
            // User selected an address - stop any autofill synthetic events
            const input = searchBoxRef.current?.querySelector('input[aria-label="Street Address"]');
            if (input) {
              input._userHasSelected = true;
            }

            const feature = res?.features?.[0];
            const props = feature?.properties || {};
            const ctx = props.context || {};
            const street = [ctx.address?.address_number, ctx.address?.street_name].filter(Boolean).join(" ");

            // Update multiple fields at once
            setFormData(prev => ({
              ...prev,
              streetAddress: street,
              zipCode: ctx.postcode?.name || "",
              city: ctx.place?.name || "",
              state: ctx.region?.name || "",
            }));
          }}
        />
      </div>
      {formErrors.validationErrors.streetAddress && <ErrorMsg>Please select a street address</ErrorMsg>}

      <Input
        type="text"
        css={{ marginTop: "10px" }}
        value={formData.addressOptional}
        onChange={(e) => updateField('addressOptional', e.target.value)}
        error={false}
        placeholder="Apt or building number (optional)"
        maxLength={100}
        autoComplete="address-line2"
        name="address-line2"
        id="address-line2"
      />

      <Row>
        <div css={{ flex: 1 }}>
          <Input
            type="text"
            value={formData.city}
            onChange={(e) => updateField('city', e.target.value)}
            error={formErrors.validationErrors.city}
            placeholder="City"
            maxLength={50}
            autoComplete="address-level2"
            name="address-level2"
            id="address-level2"
          />
          {formErrors.validationErrors.city && <ErrorMsg>Please enter the city</ErrorMsg>}
        </div>
        <div css={{ flex: 1, marginLeft: "6px" }}>
          <SelectWrapper>
            <Select
              value={formData.state}
              onChange={(e) => updateField('state', e.target.value)}
              error={formErrors.validationErrors.state}
              placeholder="State"
              autoComplete="address-level1"
              name="address-level1"
              id="address-level1"
            >
              <option value="">State</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
            <Arrow />
          </SelectWrapper>
          {formErrors.validationErrors.state && <ErrorMsg>Please select a state</ErrorMsg>}
        </div>
      </Row>

      <Input
        type="text"
        value={formData.zipCode}
        onChange={handleZipCodeChange}
        error={formErrors.validationErrors.zipCode}
        placeholder="ZIP Code"
        maxLength={12}
        autoComplete="postal-code"
        name="postal-code"
        id="postal-code"
      />
      {formErrors.validationErrors.zipCode && <ErrorMsg>Please enter the ZIP code</ErrorMsg>}
      {showSaveButton && (
        <Row style={{ marginTop: 30 }}>
          <SaveButton onClick={handleSave}>Save Address</SaveButton>
        </Row>
      )}
      {showCancelButton && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
        </div>
      )}
    </FormContainer>
  );
};

export default AddressForm;
